import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { TextField, MenuItem, Checkbox } from '@mui/material'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded'
import {
  createIncidentRoutine,
  selectIncidentMutationLoading,
  selectIncidentMutationError,
} from 'features/incidents/ducks'
import { INCIDENT_CATEGORIES } from 'features/incidents/constants'
import SeverityQuickEdit from 'components/SeverityQuickEdit'

const SEVERITIES = [
  { value: 'critical', label: 'Critical' },
  { value: 'high',     label: 'High' },
  { value: 'medium',   label: 'Medium' },
  { value: 'low',      label: 'Low' },
]

const KNOWN_CATEGORY_VALUES = INCIDENT_CATEGORIES.map(c => c.value)
const normaliseCategory = (value) => {
  if (!value) return 'other'
  return KNOWN_CATEGORY_VALUES.includes(value) ? value : 'other'
}

const formatLocalDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

const IncidentReviewStep = ({
  buildingPassportId,
  buildingPassportName,
  narrative,
  suggestions,
  attachmentIds = [],
  onCancel,
}) => {
  const dispatch = useDispatch()
  const submitting = useSelector(selectIncidentMutationLoading)
  const mutationError = useSelector(selectIncidentMutationError)
  const [attempted, setAttempted] = useState(false)

  const [category, setCategory] = useState('')
  const [severity, setSeverity] = useState('medium')
  const [incidentType, setIncidentType] = useState('incident')
  const [occurredAt, setOccurredAt] = useState('')
  const [actions, setActions] = useState([])

  useEffect(() => {
    if (!suggestions) return
    setCategory(normaliseCategory(suggestions.category))
    setSeverity(suggestions.severity || 'medium')
    setOccurredAt(
      suggestions.occurredAt ? suggestions.occurredAt.slice(0, 16) : ''
    )
    setActions((suggestions.suggestedActions || []).map(a => ({
      ...a,
      _selected: true,
      // Normalise so the controlled inputs don't choke on missing values.
      severity: a.severity || 'medium',
      suggestedDueBy: formatLocalDate(a.suggestedDueBy),
    })))
  }, [suggestions])

  if (!suggestions) return null

  const toggle = (idx) => {
    setActions(actions.map((a, i) => i === idx ? { ...a, _selected: !a._selected } : a))
  }
  const setActionField = (idx, field, value) => {
    setActions(actions.map((a, i) => i === idx ? { ...a, [field]: value } : a))
  }
  const addCustomAction = () => {
    setActions([...actions, {
      title: '',
      description: '',
      severity: 'medium',
      suggestedDueBy: '',
      _selected: true,
      _custom: true,
    }])
  }
  const removeAction = (idx) => {
    setActions(actions.filter((_, i) => i !== idx))
  }

  const submit = () => {
    setAttempted(true)
    dispatch(createIncidentRoutine({
      buildingPassportId,
      narrative,
      category,
      severity,
      incidentType,
      occurredAt: occurredAt
        ? new Date(occurredAt).toISOString()
        : new Date().toISOString(),
      attachmentIds,
      suggestedActions: actions
        .filter(a => a._selected && (a.title || '').trim().length > 0)
        .map(({ _selected, _custom, ...rest }) => ({
          ...rest,
          title: rest.title.trim(),
          suggestedDueBy: rest.suggestedDueBy || null,
        })),
    }))
  }

  const selectedCount = actions.filter(a => a._selected).length
  const totalCount = actions.length

  return (
    <Wrap>
      {buildingPassportName && (
        <BpContextBanner>
          <HomeWorkRoundedIcon style={{ fontSize: 16 }} />
          Reporting incident for <strong>{buildingPassportName}</strong>
        </BpContextBanner>
      )}

      {suggestions.aiError && (
        <Banner>
          AI was unavailable when this incident was being analysed. Review the
          fields below and edit anything that doesn&rsquo;t look right.
        </Banner>
      )}

      <Section>
        <SectionHead>
          <SectionTitle>Incident details</SectionTitle>
          <SectionHint>
            Guru filled these in from your description. Tweak anything that&rsquo;s wrong.
          </SectionHint>
        </SectionHead>
        <Grid>
          <Field>
            <FieldLabel>Type</FieldLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={incidentType}
              onChange={e => setIncidentType(e.target.value)}
            >
              <MenuItem value='accident'>Accident</MenuItem>
              <MenuItem value='incident'>Incident</MenuItem>
              <MenuItem value='near_miss'>Near miss</MenuItem>
            </TextField>
          </Field>
          <Field>
            <FieldLabel>Category</FieldLabel>
            <TextField
              select
              required
              fullWidth
              size="small"
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              {INCIDENT_CATEGORIES.map(c => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
          </Field>
          <Field>
            <FieldLabel>Severity</FieldLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={severity}
              onChange={e => setSeverity(e.target.value)}
            >
              {SEVERITIES.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Field>
          <Field>
            <FieldLabel>When</FieldLabel>
            <TextField
              type="datetime-local"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={occurredAt}
              onChange={e => setOccurredAt(e.target.value)}
            />
          </Field>
        </Grid>
      </Section>

      <Section>
        <SectionHead>
          <SectionTitle>
            <AutoAwesomeOutlinedIcon style={{ fontSize: 16, color: '#D24724' }} />
            Suggested remedials
            {totalCount > 0 && (
              <SuggestedCount>
                <strong>{selectedCount}</strong> of {totalCount} selected
              </SuggestedCount>
            )}
          </SectionTitle>
          <SectionHint>
            Each one will be created as an open remedial you can convert into a task later.
            Click a severity chip to re-triage it before filing.
          </SectionHint>
        </SectionHead>

        {actions.length === 0 && (
          <Empty>
            No follow-up remedials were suggested. You can still log the incident on its own.
          </Empty>
        )}

        {actions.length > 0 && (
          <ActionList>
            {actions.map((a, i) => (
              <ActionRow key={i} $muted={!a._selected}>
                <CheckCol>
                  <Checkbox
                    checked={!!a._selected}
                    onChange={() => toggle(i)}
                    size="small"
                  />
                </CheckCol>
                <MainCol>
                  {a._custom ? (
                    <>
                      <CustomTitleInput
                        autoFocus={!a.title}
                        value={a.title}
                        placeholder='What needs doing?'
                        onChange={e => setActionField(i, 'title', e.target.value)}
                      />
                      <CustomDescInput
                        value={a.description}
                        placeholder='Optional detail…'
                        onChange={e => setActionField(i, 'description', e.target.value)}
                      />
                    </>
                  ) : (
                    <>
                      <RowTitle>{a.title}</RowTitle>
                      {a.description && <RowDesc>{a.description}</RowDesc>}
                    </>
                  )}
                  <RowMeta>
                    <SeverityQuickEdit
                      value={a.severity}
                      disabled={!a._selected}
                      size="sm"
                      onChange={(next) => setActionField(i, 'severity', next)}
                    />
                    <DueWrap>
                      <EventOutlinedIcon style={{ fontSize: 14, color: '#94a3b8', flexShrink: 0 }} />
                      <DueInput
                        type="date"
                        value={a.suggestedDueBy || ''}
                        disabled={!a._selected}
                        onChange={e => setActionField(i, 'suggestedDueBy', e.target.value)}
                      />
                      {a.suggestedDueBy && a._selected && (
                        <ClearDueBtn
                          type="button"
                          onClick={() => setActionField(i, 'suggestedDueBy', '')}
                          aria-label="Clear due date"
                        >
                          ×
                        </ClearDueBtn>
                      )}
                    </DueWrap>
                  </RowMeta>
                </MainCol>
                {a._custom ? (
                  <RemoveCustomBtn
                    type='button'
                    onClick={() => removeAction(i)}
                    aria-label='Remove this remedial'>
                    ×
                  </RemoveCustomBtn>
                ) : (
                  <DismissBtn
                    type='button'
                    $dismissed={!a._selected}
                    onClick={() => toggle(i)}>
                    {a._selected ? "Don't add" : 'Add back'}
                  </DismissBtn>
                )}
              </ActionRow>
            ))}
          </ActionList>
        )}

        <AddActionBtn type='button' onClick={addCustomAction}>
          <AddActionPlus>+</AddActionPlus>
          Add another remedial
        </AddActionBtn>
      </Section>

      {attempted && !submitting && mutationError && (
        <SubmitError role='alert'>The incident was not logged. Your details are still here, so you can try again.</SubmitError>
      )}
      <Footer>
        {onCancel && <SecondaryButton type="button" onClick={onCancel}>Back</SecondaryButton>}
        <PrimaryButton type="button" onClick={submit} disabled={submitting || !category}>
          {submitting
            ? 'Logging…'
            : selectedCount > 0
              ? `Log incident & add ${selectedCount} remedial${selectedCount === 1 ? '' : 's'}`
              : 'Log incident'}
        </PrimaryButton>
      </Footer>
    </Wrap>
  )
}

export default IncidentReviewStep

const Wrap = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) { padding: 16px; }
`

const BpContextBanner = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  align-self: flex-start;
  padding: 8px 14px;
  background: rgba(51, 190, 204, 0.08);
  border: 1px solid rgba(51, 190, 204, 0.26);
  color: #1f8c97;
  border-radius: 999px;
  font-size: 12.5px;
  strong { font-weight: 800; }
`

const AddActionBtn = styled.button`
  font: inherit;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%;
  padding: 14px;
  margin-top: 4px;
  background: transparent;
  border: 1.5px dashed #cbd5e1;
  border-radius: 12px;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 160ms ease;
  &:hover {
    border-color: #33becc;
    color: #1f8c97;
    background: rgba(51, 190, 204, 0.04);
  }
`

const AddActionPlus = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  border-radius: 50%;
  background: rgba(51, 190, 204, 0.12);
  color: #1f8c97;
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
`

const CustomTitleInput = styled.input`
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  border: none;
  border-bottom: 1.5px dashed #cbd5e1;
  background: transparent;
  outline: none;
  padding: 2px 0 4px;
  width: 100%;
  &:focus { border-bottom-color: #33becc; border-bottom-style: solid; }
  &::placeholder { color: #94a3b8; font-weight: 500; }
`

const CustomDescInput = styled.input`
  font: inherit;
  font-size: 12.5px;
  color: #475569;
  border: none;
  background: transparent;
  outline: none;
  padding: 2px 0;
  width: 100%;
  &::placeholder { color: #b6c0cd; }
`

const DismissBtn = styled.button`
  font: inherit;
  align-self: flex-start;
  flex-shrink: 0;
  padding: 4px 11px;
  background: ${({ $dismissed }) => $dismissed ? 'rgba(42,166,179,0.08)' : 'transparent'};
  border: 1px solid ${({ $dismissed }) => $dismissed ? 'rgba(42,166,179,0.30)' : '#e2e8f0'};
  color: ${({ $dismissed }) => $dismissed ? '#1f8c97' : '#64748b'};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  white-space: nowrap;
  transition: all 160ms ease;
  &:hover { border-color: #94a3b8; color: #0f172a; }
`

const RemoveCustomBtn = styled.button`
  font: inherit;
  align-self: flex-start;
  width: 26px; height: 26px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: #fee2e2; color: #b00020; }
`

const Banner = styled.div`
  background: #fff7e6;
  border: 1px solid #ffd591;
  color: #92400e;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SectionHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const SectionTitle = styled.h3`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`

const SuggestedCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-left: 4px;
  strong { color: #0f172a; font-weight: 700; }
`

const SectionHint = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  max-width: 720px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ActionRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: flex-start;
  padding: 12px 14px;
  background: ${({ $muted }) => ($muted ? '#fafbfc' : 'white')};
  border: 1px solid ${({ $muted }) => ($muted ? '#e5e7eb' : '#e5e7eb')};
  border-radius: 10px;
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
  transition: opacity 0.15s, background 0.15s;
`

const CheckCol = styled.div`
  margin-left: -6px;
  margin-top: -4px;
  flex-shrink: 0;
`

const MainCol = styled.div`
  flex: 1;
  min-width: 0;
`

const RowTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.4;
`

const RowDesc = styled.p`
  margin: 4px 0 0 0;
  font-size: 12px;
  color: #475569;
  line-height: 1.5;
`

const RowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
`

const DueWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px 2px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: white;
`

const DueInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: #0f172a;
  padding: 2px 0;
  min-width: 110px;
  &:disabled { color: #94a3b8; }
  &::-webkit-calendar-picker-indicator { opacity: 0.5; }
`

const ClearDueBtn = styled.button`
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
  &:hover { color: #475569; }
`

const Empty = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
  padding: 12px 14px;
  border: 1px dashed #e5e7eb;
  border-radius: 10px;
  background: #fafbfc;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;
`

const PrimaryButton = styled.button`
  padding: 10px 18px;
  background: #D24724;
  color: white;
  border: 1px solid #D24724;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover:not(:disabled) { background: #b53a1d; border-color: #b53a1d; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const SecondaryButton = styled.button`
  padding: 10px 18px;
  background: white;
  color: #475569;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #f8fafc; color: #0f172a; }
`

const SubmitError = styled.p`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fdecea;
  color: #b00020;
  font-size: 14px;
`
